import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import '../../lib/intersectionObserverPolyfill'; 
import Modal from '../Common/Modal';

import { activityService, itemsPerPage } from '../../Services';

import {ReactComponent as Loading} from '../../images/icons/General/loading-01.svg';
import {ReactComponent as Edit} from '../../images/icons/General/edit-01.svg';
import {ReactComponent as Trash} from '../../images/icons/General/trash-01.svg';

export const Activity = () => {
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [list, setList] = useState([]);
    const [info, setInfo] = useState({});
    const [sortField, setSortField] = useState('startDate');
    const [sortOrder, setSortOrder] = useState(false);
    const [page, setPage] = useState(-1);
    const [hasMore, setHasMore] = useState(true);
    const [deleteId, setDeleteId] = useState(null);
    const loaderRef = useRef(null);
    const tableRef = useRef(null);
    const initialPageRef = useRef(Math.max(parseInt(searchParams.get('page')) || 1, 1) - 1);
    const firstLoadRef = useRef(true);
    const scrollToIndexRef = useRef(null);
    const remember = useSelector((state) => state.org.remember);
    const userId = useSelector((state) => state[remember ? 'localUser' : 'user'].id);
    const permissions = useSelector((state) => state[remember ? 'localUser' : 'user'].permissions)?.split(',');

    const resetValues = () => {
        setList([]);
        setInfo({});
        setPage(-1);
        setHasMore(true);
    }

    const sort = (e) => {
        if (e.target.id === sortField)
            setSortOrder(!sortOrder)
        else 
            setSortField(e.target.id);

        resetValues();
    }

    const deleteActivity = async (id) => {
        setDeleteId(id);
    }

    const fetchData = useCallback(async (pageNum, pagesCount, sortFieldValue, sortOrderValue) => {
        const res = await activityService.activityList(params.slug || '', pageNum, sortFieldValue, sortOrderValue, pagesCount);
        setList(prev => {
            const seen = new Set(prev.map(i => i.id));
            return [...prev, ...res.list.filter(i => !seen.has(i.id))];
        });

        if (pagesCount > 1)
            scrollToIndexRef.current = itemsPerPage * (pagesCount - 1);

        if (res.info)
            setInfo(res.info);

        if (res.list.length < itemsPerPage * pagesCount)
            setHasMore(false);
      }, [params.slug]);

    useEffect(() => {
        resetValues();
    }, [params.slug]);

    useEffect(() => {
        const observer = new IntersectionObserver(
        (entries) => {
            if (entries[0].isIntersecting && hasMore) {
                setPage((prev) => prev === -1 && firstLoadRef.current ? initialPageRef.current : prev + 1);
            }
        },
        { threshold: 0.1 }
        );

        const loader = loaderRef.current;
        if (loader) observer.observe(loader);

        return () => {
            if (loader) observer.unobserve(loader);
        };
    }, [hasMore]);

    useEffect(() => {
        if (page < 0) return;
        // on the first load fetch all pages up to the one from the url in one request
        const pagesCount = firstLoadRef.current ? page + 1 : 1;
        firstLoadRef.current = false;
        fetchData(page + 1 - pagesCount, pagesCount, sortField, sortOrder);
    }, [fetchData, page, sortField, sortOrder]);

    useEffect(() => {
        if (page < 0) return;
        const urlPage = page > 0 ? String(page + 1) : null;
        if (searchParams.get('page') === urlPage) return;
        const newParams = new URLSearchParams(searchParams);
        if (urlPage)
            newParams.set('page', urlPage);
        else
            newParams.delete('page');
        setSearchParams(newParams, { replace: true });
    }, [page, searchParams, setSearchParams]);

    useEffect(() => {
        const index = scrollToIndexRef.current;
        if (index === null) return;
        scrollToIndexRef.current = null;
        const row = tableRef.current?.tBodies[0]?.rows[index];
        if (row)
            row.scrollIntoView({ block: 'start' });
    }, [list]);
    
    return (
        <>
            <h1>{info.label ? info.label + ' ' : ''}Activities</h1>
            <div className="bordered-content">
                {permissions.includes('Add activity') ? <div className="header">
                    <Link to={`/activities/${params.slug}/add`}><button className="primary">Add activity</button></Link>
                </div> : ''}
                <table id="history-table" ref={tableRef}>
                    <thead>
                        <tr>
                            <th className={`sortable ${sortField === 'name' ? (sortOrder ? 'down' : 'up') : ''}`} id="name" onClick={sort}>Title</th>
                            <th className={`sortable ${sortField === 'startDate' ? (sortOrder ? 'down' : 'up') : ''}`} id="startDate" onClick={sort}>Start date</th>
                            <th className={`sortable ${sortField === 'endDate' ? (sortOrder ? 'down' : 'up') : ''}`} id="endDate" onClick={sort}>End date</th>
                            {info.hasLocation ? <th className="light">Location</th> : ''}
                            <th className="light">Person in charge</th>
                            {info.hasCost ? <th className="light">Cost</th> : ''}
                            <th className="light">Maximum singups</th>
                            <th className="table-icon light" style={{width: '60px'}}></th>
                            <th className="table-icon light" style={{width: '60px'}}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.length ? list.map(item => {
                            return (
                                <tr key={item.id}>
                                    <td><Link to={`/activities/${params.slug}/${item.id}`}>{(item.name || (item.description)?.slice(0, 100) + '...')}</Link></td>
                                    <td><strong className="mobile">Start date: </strong>{item.startDate}</td>
                                    <td><strong className="mobile">End date: </strong>{item.endDate}</td>
                                    {info.hasLocation ? <td><strong className="mobile">Location: </strong>{item.location}</td> : ''}
                                    <td><strong className="mobile">Person in charge: </strong>{item.personInCharge}</td>
                                    {info.hasCost ? <td><strong className="mobile">Cost: </strong>{item.cost}</td> : ''}
                                    <td><strong className="mobile">Maximum singups: </strong>{item.maxSignups}</td>
                                    <td className="desktop">
                                        {permissions.includes('Edit activity') ? 
                                        <Link to={`/activities/${params.slug}/edit/${item.id}`}>
                                            <Edit className="pointer" />
                                        </Link> : ''}
                                    </td>
                                    <td className="desktop">
                                        {permissions.includes('Edit activity') ? 
                                            <Trash className="pointer" onClick={() => deleteActivity(item.id)} /> : ''}
                                    </td>
                                    {permissions.includes('Edit activity') ? 
                                    <td className="mobile">
                                        <Link to={`/activities/${params.slug}/edit/${item.id}`}>
                                            <span className="mobile label link">Edit</span>
                                        </Link>
                                        <span className="mobile label link" onClick={() => deleteActivity(item.id)}>Delete</span>
                                    </td> : ''}
                                </tr>
                            )}
                        ) : (hasMore ? <></> : <tr><td colSpan="9" className="load-more">No activities found</td></tr>)}
                        {hasMore && <tr ref={loaderRef}><td colSpan="9" className="load-more">Loading more...</td></tr>}
                    </tbody>
                </table>
            </div>
            {deleteId ? <ModalDeleteActivity setClose={() => setDeleteId(null)} activityId={deleteId} setList={setList} /> : ''}
        </>
    );
};

export default Activity;

const ModalDeleteActivity = ({setClose, activityId, setList}) => {
    const [loading, setLoading] = useState(false);

    const deleteActivity = async () => {  
        setLoading(true);
        const res = await activityService.activityDelete(activityId);
        if (!res.error) {
            setList(prev => prev.filter(i => i.id !== activityId));
            setClose();
        }
        setLoading(false);
    }

    return (
        <Modal setClose={setClose} icon={<Trash />}>
            <h4 className="center">Are you sure you want to delete this activity?</h4>
            <div className="buttons">
                <button className="default" onClick={setClose}>
                    Cancel
                </button>
                <button className="primary" onClick={deleteActivity}>
                    {loading ? <Loading className="btn-loader" alt="loader" /> : ''}Delete
                </button>
            </div>
        </Modal>
    )
}